import React, { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell
} from "recharts";
import { Device } from "@/types/device";

interface PlatformBreakdownChartProps {
  devices: Device[];
}

const STATUS_COLORS = {
  active: "#6366F1",
  inactive: "#FF8C66",
  pending: "#4ADE80",
};

export function PlatformBreakdownChart({ devices }: PlatformBreakdownChartProps) {
  const data = useMemo(() => {
    const map: Record<string, { active: number; inactive: number; pending: number }> = {};
    devices.forEach((d) => {
      const os = (d.os_type || "unknown").toLowerCase();
      const label = os === "ios" ? "iOS" : os === "macos" ? "macOS" : os === "tvos" ? "tvOS" : os.charAt(0).toUpperCase() + os.slice(1);
      if (!map[label]) map[label] = { active: 0, inactive: 0, pending: 0 };
      const status = (d.status || "unknown").toLowerCase();
      if (status === "active") map[label].active++;
      else if (status === "inactive") map[label].inactive++;
      else if (status === "pending") map[label].pending++;
    });
    return Object.entries(map)
      .map(([name, counts]) => ({ name, ...counts, total: counts.active + counts.inactive + counts.pending }))
      .sort((a, b) => b.total - a.total);
  }, [devices]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No device data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip
          contentStyle={{ fontSize: 12 }}
          formatter={(value: any, name: string) => [`${value} devices`, name]}
          labelFormatter={(label) => `Platform: ${label}`}
        />
        <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="active" stackId="a" name="Active" fill={STATUS_COLORS.active} />
        <Bar dataKey="inactive" stackId="a" name="Inactive" fill={STATUS_COLORS.inactive} />
        <Bar dataKey="pending" stackId="a" name="Pending" fill={STATUS_COLORS.pending} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
